import { Anchor } from "@omni/ui/components/Anchor/anchor.component";
import { attempt } from "@omni/utils/function.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import queryString from "query-string";
import { FC, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import {
    blendColorsLinear,
    isDarkColor,
    sortColorsByLuminance
} from "@/components/PaintConfigurator/helpers/color.helper";
import { isPaintConfiguration } from "@/components/PaintConfigurator/helpers/config.helper";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import { getPaintCollection } from "@/redux/modules/value/paintCollection/paintCollection.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";

import { ProductConfigurationVisualProps } from "../../productConfiguration.types";
import { PaintConfiguration } from "./paintConfigurator.types";

const PaintConfigurator: FC<
    ProductConfigurationVisualProps<PaintConfiguration>
> = ({
    setConfigurationValid,
    onConfigurationChange,
    configuration,
    metaConfiguration: { paintConfiguratorPath } = {},
    className
}) => {
    const { t } = useGranite();
    const isClientSide = useIsClientSide();
    const cartLoading = useSelector(CartSelectors.isLoadingCart);
    const collection = useSelector(getPaintCollection);

    const queryParams = useMemo(() => {
        if (!isClientSide) return null;

        return attempt(() => {
            const qs = queryString.parse(window.location.search);
            if (!qs) return null;

            return qs["pc"] as string;
        }, null);
    }, [isClientSide]);

    useEffect(() => {
        if (!queryParams) return;

        const decodeValue = attempt(() => {
            return JSON.parse(decodeURIComponent(atob(queryParams)));
        }, null);

        if (isPaintConfiguration(decodeValue)) {
            onConfigurationChange(decodeValue);
            setConfigurationValid(true);
        }
    }, [onConfigurationChange, setConfigurationValid, queryParams]);

    useEffect(() => {
        if (isPaintConfiguration(configuration)) {
            setConfigurationValid(true);
        }
    }, [configuration, setConfigurationValid]);

    if (
        !isClientSide ||
        cartLoading ||
        (!configuration?.paintInfo?.colorHex && !paintConfiguratorPath)
    ) {
        return null;
    }

    const hexColor = configuration?.paintInfo?.colorHex;

    if (!hexColor && paintConfiguratorPath) {
        const colors =
            collection?.families
                .map((f) => {
                    return blendColorsLinear(f.colors.map((c) => c.hexValue));
                })
                .filter(isDefined) || [];

        const sortedColors = sortColorsByLuminance(colors, (c) => c).slice(
            0,
            6
        );
        const lengthDiff = colors.length - sortedColors.length;

        return (
            <div className={cn("tw:mb-3", className)}>
                <p className={cn("tw:w-full tw:mb-2")}>
                    {t("ecommerce.label.paintConfigurator.selectAColor")}
                </p>
                <Anchor
                    color="gray"
                    className={cn("tw:text-foreground tw:bg-white tw:py-4")}
                    outline
                    fullWidth
                    href={paintConfiguratorPath}
                    aria-label={t(
                        "ecommerce.label.paintConfigurator.anchor.label"
                    )}
                    target="_self"
                >
                    <div
                        className={cn(
                            "tw:flex tw:gap-2 tw:items-center tw:flex-wrap"
                        )}
                    >
                        <div
                            className={cn("tw:flex tw:items-center tw:gap-0.5")}
                        >
                            <div className={cn("tw:flex tw:-space-x-0.5")}>
                                {sortedColors.map((color, i) => (
                                    <div
                                        key={i}
                                        className={cn(
                                            "tw:w-3 tw:h-3 tw:rounded-full tw:border tw:border-white/60"
                                        )}
                                        style={{ backgroundColor: `${color}` }}
                                    />
                                ))}
                            </div>
                            {lengthDiff > 0 && (
                                <div className={cn("")}>
                                    <span
                                        className={cn(
                                            "tw:text-xs tw:text-foreground/40 tw:font-semibold"
                                        )}
                                    >
                                        (+{lengthDiff})
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className={cn("tw:text-sm")}>
                            {t(
                                "ecommerce.label.paintConfigurator.anchor.label"
                            )}{" "}
                            <span className={cn("las la-angle-right")}></span>
                        </div>
                    </div>
                </Anchor>
            </div>
        );
    }

    if (!hexColor) {
        return null;
    }

    return (
        <div className={cn("tw:mb-3", className)}>
            <p className={cn("tw:w-full tw:mb-2")}>
                {t("ecommerce.label.paintConfigurator.selectedColor")}
            </p>
            <div
                className={cn(
                    "tw:w-full tw:rounded tw:ring tw:ring-grey-light tw:ring-offset-2"
                )}
                style={{
                    backgroundColor: `#${hexColor}`
                }}
            >
                <p
                    className={cn(
                        "tw:p-3",
                        isDarkColor(hexColor)
                            ? "tw:text-background"
                            : "tw:text-foreground"
                    )}
                >
                    <span className={cn("tw:text-sm tw:pr-1")}>
                        {configuration?.paintInfo?.colorName}
                    </span>
                </p>
            </div>
            <p className={cn("tw:mt-2")}>
                <a
                    href={`${paintConfiguratorPath}?pc=${queryParams}`}
                    target="_self"
                    aria-label={t(
                        "ecommerce.label.paintConfigurator.anchor.edit"
                    )}
                >
                    {t("ecommerce.label.paintConfigurator.anchor.edit")}
                </a>
            </p>
        </div>
    );
};

export { PaintConfigurator };
